import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash, MapPinLine, MapPinArea, X, PencilLine, Check, CaretLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { cn } from '../lib/utils';
import { createPortal } from 'react-dom';

const LocationVariantsModal = ({ 
  isOpen, 
  onClose,
  onSave,
  supabase, 
  locations = [], 
  settings_id = null,
  setting = null
}) => {
  const [groupings, setGroupings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGrouping, setSelectedGrouping] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [assignedLocations, setAssignedLocations] = useState({});
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [editingVariantName, setEditingVariantName] = useState('');
  const initializedRef = useRef(false);
  const popupRef = useRef(null);

  // Mobile state management
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeTab, setActiveTab] = useState('versions'); // 'versions' or 'locations'

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside to close popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Initialize with database data when modal opens
  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current) return;
    initializedRef.current = true;

    initializeGroupings();
  }, [isOpen, settings_id]);

  const initializeGroupings = async () => {
    if (!settings_id) return;

    setLoading(true);
    const { groupings: fetchedGroupings, assignments: fetchedAssignments } = 
      await fetchGroupingsFromDB(settings_id);

    if (fetchedGroupings.length > 0) {
      setGroupings(fetchedGroupings);
      setAssignedLocations(fetchedAssignments);
      setSelectedGrouping(fetchedGroupings[0].id);
    } else {
      // Create default group if none exist
      const defaultId = 'default_group';
      const defaultGroup = {
        id: defaultId,
        name: 'Main Store',
        isNew: true
      };

      setGroupings([defaultGroup]);
      const allLocationIds = locations.map(loc => loc.id);
      setAssignedLocations({ [defaultId]: allLocationIds });
      setSelectedGrouping(defaultId);
    }
    setLoading(false);
  };

  // Fetch groupings from database
  const fetchGroupingsFromDB = async (settingsId) => {
    try {
      // Fetch groupings for this settings_id
      const { data: groupingsData, error: groupingsError } = await supabase
        .from('brain_groupings')
        .select('*')
        .eq('settings_id', settingsId)
        .order('created_at');

      if (groupingsError) throw groupingsError;

      if (!groupingsData || groupingsData.length === 0) {
        return { groupings: [], assignments: {} };
      }

      // Fetch location assignments for these groupings
      const groupingIds = groupingsData.map(g => g.id);
      const { data: locationsData, error: locationsError } = await supabase
        .from('brain_grouping_locations')
        .select('grouping_id, location_id')
        .in('grouping_id', groupingIds);

      if (locationsError) throw locationsError;

      // Format assignments into the expected structure
      const formattedAssignments = {};
      (locationsData || []).forEach(row => {
        if (!formattedAssignments[row.grouping_id]) {
          formattedAssignments[row.grouping_id] = [];
        }
        formattedAssignments[row.grouping_id].push(row.location_id);
      });

      return { 
        groupings: groupingsData, 
        assignments: formattedAssignments 
      };
    } catch (err) {
      console.error('Error fetching groupings:', err);
      toast.error('Failed to load store versions');
      return { groupings: [], assignments: {} };
    }
  };

  // Handle creating a new grouping
  const handleCreateGrouping = async () => {
    if (!newGroupName.trim() || !settings_id) return;

    try {
      setLoading(true);

      // Create new grouping in database
      const { data: newGrouping, error } = await supabase
        .from('brain_groupings')
        .insert({
          settings_id: settings_id,
          name: newGroupName.trim()
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      setGroupings(prev => [...prev, newGrouping]);
      setNewGroupName('');
      setIsCreating(false);
      setSelectedGrouping(newGrouping.id);
      setAssignedLocations(prev => ({ ...prev, [newGrouping.id]: [] }));

      // On mobile, automatically switch to locations tab after creating
      if (isMobile) {
        setActiveTab('locations');
      }

      toast.success('New version created');
    } catch (err) {
      console.error('Error creating grouping:', err);
      toast.error('Failed to create version');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = () => {
    if (onSave) {
      onSave({ groupings, assignedLocations });
    }
    onClose();
  };

  // Handle grouping selection
  const handleSelectGrouping = (groupingId) => {
    setSelectedGrouping(groupingId);
    // On mobile, automatically switch to locations tab when selecting a version
    if (isMobile) {
      setActiveTab('locations');
    }
  };

  // Fixed location toggle with proper counting logic
  const handleLocationToggle = async (groupingId, locationId) => {
    const currentAssigned = assignedLocations[groupingId] || [];
    const isAssigned = currentAssigned.includes(locationId);

    try {
      if (isAssigned) {
        // Remove location assignment from database
        await supabase
          .from('brain_grouping_locations')
          .delete()
          .eq('grouping_id', groupingId)
          .eq('location_id', locationId);

        // Update state - just remove from current group
        setAssignedLocations(prev => ({
          ...prev,
          [groupingId]: prev[groupingId]?.filter(id => id !== locationId) || []
        }));
      } else {
        // FIRST: Remove location from ALL other groups in database
        const currentAssignments = { ...assignedLocations };
        const removalPromises = [];

        for (const [gId, locIds] of Object.entries(currentAssignments)) {
          if (gId !== groupingId && locIds.includes(locationId)) {
            removalPromises.push(
              supabase
                .from('brain_grouping_locations')
                .delete()
                .eq('grouping_id', gId)
                .eq('location_id', locationId)
            );
          }
        }

        // Wait for all removals to complete
        if (removalPromises.length > 0) {
          await Promise.all(removalPromises);
        }

        // THEN: Add to new group in database
        await supabase
          .from('brain_grouping_locations')
          .insert({
            grouping_id: groupingId,
            location_id: locationId
          });

        // FINALLY: Update state with correct counts
        setAssignedLocations(prev => {
          const updated = { ...prev };

          // Remove from all other groups
          Object.keys(updated).forEach(gId => {
            if (gId !== groupingId && updated[gId]) {
              updated[gId] = updated[gId].filter(id => id !== locationId);
            }
          });

          // Add to new group (ensure no duplicates)
          if (!updated[groupingId]) {
            updated[groupingId] = [];
          }
          if (!updated[groupingId].includes(locationId)) {
            updated[groupingId] = [...updated[groupingId], locationId];
          }

          return updated;
        });
      }
    } catch (err) {
      console.error('Error toggling location:', err);
      toast.error('Failed to update store assignment');
    }
  };

  // Handle grouping deletion
  const handleDeleteGrouping = async (groupingId) => {
    try {
      setLoading(true);

      // Delete location assignments first
      await supabase
        .from('brain_grouping_locations')
        .delete()
        .eq('grouping_id', groupingId);

      // Delete the grouping
      await supabase
        .from('brain_groupings')
        .delete()
        .eq('id', groupingId);

      // Update local state
      setGroupings(prev => prev.filter(group => group.id !== groupingId));
      setSelectedGrouping(groupings.find(g => g.id !== groupingId)?.id || null);

      const updatedAssignments = { ...assignedLocations };
      delete updatedAssignments[groupingId];
      setAssignedLocations(updatedAssignments);

      toast.success('Version deleted');
    } catch (err) {
      console.error('Error deleting grouping:', err);
      toast.error('Failed to delete version');
    } finally {
      setLoading(false);
    }
  };

  // Handle variant name editing
  const handleEditVariantName = async (variantId) => {
    if (!editingVariantName.trim()) {
      setEditingVariantId(null);
      setEditingVariantName('');
      return;
    }

    try {
      const { error } = await supabase
        .from('brain_groupings')
        .update({ name: editingVariantName.trim() })
        .eq('id', variantId);

      if (error) throw error;

      // Update local state
      setGroupings(prev => prev.map(g => 
        g.id === variantId ? { ...g, name: editingVariantName.trim() } : g
      ));

      setEditingVariantId(null);
      setEditingVariantName('');
      toast.success('Version name updated');

    } catch (err) {
      console.error('Error updating variant name:', err);
      toast.error('Failed to update version name');
    }
  };

  // Start editing variant name
  const startEditingVariant = (variant) => {
    setEditingVariantId(variant.id);
    setEditingVariantName(variant.name);
  };

  // Cancel editing
  const cancelEditingVariant = () => {
    setEditingVariantId(null);
    setEditingVariantName('');
  };

  if (!isOpen) return null;

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 300,
        duration: 0.4 
      }
    },
    exit: { 
      scale: 0.98, 
      opacity: 0,
      y: 10,
      transition: { duration: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }),
    exit: { 
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 }
    },
    removed: {
      opacity: 0,
      x: -50,
      transition: { duration: 0.2 }
    }
  };

  // Portal the modal to ensure it's at the root level of the DOM
  const modalContent = (
    <AnimatePresence>
      <motion.div 
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={backdropVariants}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4 overflow-hidden"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={onClose}
      >
        <motion.div 
          ref={popupRef}
          variants={popupVariants}
          className={cn(
            "bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col",
            isMobile 
              ? "w-full h-full max-h-full" 
              : "w-full max-w-5xl max-h-[85vh]"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b border-gray-100/80 sticky top-0 bg-white z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
                <MapPinArea size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
              </div>
              <div>
                <h2 className="font-medium text-base sm:text-lg text-gray-900">Location Versions</h2>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
                  Create different versions of "{setting?.name || 'this setting'}" for each location
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSaveChanges}
                className="h-7 sm:h-8 px-3 sm:px-4 rounded-xl bg-black text-white text-xs sm:text-sm font-medium
                  hover:bg-gray-800 transition-colors"
                aria-label="Save"
              >
                Save
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="rounded-full h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X size={16} className="sm:w-[18px] sm:h-[18px]" />
              </motion.button>
            </div>
          </motion.div>

          {/* Mobile Tab Navigation */}
          {isMobile && (
            <motion.div 
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="border-b px-5 py-3 bg-gray-50"
            >
              <div className="flex rounded-xl bg-white p-1 shadow-sm border border-gray-100/60">
                <button
                  onClick={() => setActiveTab('versions')}
                  className={cn(
                    "flex-1 py-2.5 px-4 text-xs sm:text-sm font-medium rounded-lg transition-all",
                    activeTab === 'versions'
                      ? "bg-black text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  )}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <MapPinArea className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Versions</span>
                    <span className={cn(
                      "text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full",
                      activeTab === 'versions' ? "bg-white/20" : "bg-gray-100"
                    )}>
                      {groupings.length}
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('locations')}
                  disabled={!selectedGrouping}
                  className={cn(
                    "flex-1 py-2.5 px-4 text-xs sm:text-sm font-medium rounded-lg transition-all",
                    activeTab === 'locations' && selectedGrouping
                      ? "bg-black text-white shadow-sm"
                      : selectedGrouping 
                      ? "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                      : "text-gray-400 cursor-not-allowed"
                  )}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <MapPinLine className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Locations</span>
                    {selectedGrouping && (
                      <span className={cn(
                        "text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full",
                        activeTab === 'locations' ? "bg-white/20" : "bg-gray-100"
                      )}>
                        {(assignedLocations[selectedGrouping] || []).length}
                      </span>
                    )}
                  </div>
                </button>
              </div>
            </motion.div>
          )}

          {/* Content */}
          <div className="flex-1 flex min-h-0">
            {loading ? (
              <div className="h-full w-full flex items-center justify-center">
                <div className="text-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="mx-auto mb-4 h-6 w-6 sm:h-8 sm:w-8 border-2 border-gray-300 border-t-black rounded-full"
                  />
                  <p className="text-gray-500 text-xs sm:text-sm">Loading versions...</p>
                </div>
              </div>
            ) : isMobile ? (
              // Mobile Layout - Tab-based
              <div className="flex-1 min-h-0">
                {activeTab === 'versions' && (
                  <VersionsTab
                    groupings={groupings}
                    selectedGrouping={selectedGrouping}
                    assignedLocations={assignedLocations}
                    isCreating={isCreating}
                    setIsCreating={setIsCreating}
                    newGroupName={newGroupName}
                    setNewGroupName={setNewGroupName}
                    editingVariantId={editingVariantId}
                    editingVariantName={editingVariantName}
                    handleCreateGrouping={handleCreateGrouping}
                    handleSelectGrouping={handleSelectGrouping}
                    handleDeleteGrouping={handleDeleteGrouping}
                    startEditingVariant={startEditingVariant}
                    cancelEditingVariant={cancelEditingVariant}
                    handleEditVariantName={handleEditVariantName}
                    setEditingVariantName={setEditingVariantName}
                    isMobile={true}
                    loading={loading}
                    itemVariants={itemVariants}
                  />
                )}

                {activeTab === 'locations' && (
                  <LocationsTab
                    selectedGrouping={selectedGrouping}
                    groupings={groupings}
                    locations={locations}
                    assignedLocations={assignedLocations}
                    handleLocationToggle={handleLocationToggle}
                    setActiveTab={setActiveTab}
                    isMobile={true}
                    itemVariants={itemVariants}
                  />
                )}
              </div>
            ) : (
              // Desktop Layout - Side by side
              <div className="flex flex-1 min-h-0">
                {/* Sidebar: Versions */}
                <div className="w-80 bg-gray-50/50 border-r border-gray-100 flex flex-col min-h-0">
                  <VersionsTab
                    groupings={groupings}
                    selectedGrouping={selectedGrouping}
                    assignedLocations={assignedLocations}
                    isCreating={isCreating}
                    setIsCreating={setIsCreating}
                    newGroupName={newGroupName}
                    setNewGroupName={setNewGroupName}
                    editingVariantId={editingVariantId}
                    editingVariantName={editingVariantName}
                    handleCreateGrouping={handleCreateGrouping}
                    handleSelectGrouping={handleSelectGrouping}
                    handleDeleteGrouping={handleDeleteGrouping}
                    startEditingVariant={startEditingVariant}
                    cancelEditingVariant={cancelEditingVariant}
                    handleEditVariantName={handleEditVariantName}
                    setEditingVariantName={setEditingVariantName}
                    isMobile={false}
                    loading={loading}
                    itemVariants={itemVariants}
                  />
                </div>

                {/* Main Content: Location Assignment */}
                <div className="flex-1 bg-white flex flex-col min-h-0">
                  <LocationsTab
                    selectedGrouping={selectedGrouping}
                    groupings={groupings}
                    locations={locations}
                    assignedLocations={assignedLocations}
                    handleLocationToggle={handleLocationToggle}
                    setActiveTab={setActiveTab}
                    isMobile={false}
                    itemVariants={itemVariants}
                  />
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  // Use a portal to render the modal at the document level
  return createPortal(modalContent, document.body);
};

// Versions Tab Component
const VersionsTab = ({
  groupings,
  selectedGrouping,
  assignedLocations,
  isCreating,
  setIsCreating,
  newGroupName,
  setNewGroupName,
  editingVariantId,
  editingVariantName,
  handleCreateGrouping,
  handleSelectGrouping,
  handleDeleteGrouping,
  startEditingVariant,
  cancelEditingVariant,
  handleEditVariantName,
  setEditingVariantName,
  isMobile,
  loading,
  itemVariants
}) => {
  return (
    <div className="flex-1 overflow-y-auto p-5 sm:p-6">
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4 sm:space-y-5"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-sm sm:text-base text-gray-900">
            Versions
          </h3>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsCreating(!isCreating)}
            className="h-8 sm:h-9 px-3 sm:px-4 rounded-xl bg-black text-white text-xs sm:text-sm font-medium
              hover:bg-gray-800 transition-colors flex items-center"
          >
            <Plus size={14} className="mr-1 sm:mr-2 sm:w-[15px] sm:h-[15px]" />
            {isMobile ? "Add" : "Add Version"}
          </motion.button>
        </div>

        {/* New Version Input */}
        <AnimatePresence>
        {isCreating && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
          >
            <div className="space-y-3">
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Version name"
                  className="w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm 
                    focus:outline-none focus:ring-2 focus:ring-black/50 focus:border-transparent
                    placeholder:text-gray-400"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateGrouping();
                  if (e.key === 'Escape') setIsCreating(false);
                }}
              />
              <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  onClick={handleCreateGrouping}
                    disabled={!newGroupName.trim() || loading}
                    className="flex-1 bg-black text-white rounded-xl h-9 sm:h-10 px-4 text-xs sm:text-sm font-medium
                      disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
                >
                  Create
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  onClick={() => setIsCreating(false)}
                    className="h-9 sm:h-10 px-4 text-gray-600 hover:bg-gray-50 rounded-xl text-xs sm:text-sm font-medium
                      border border-gray-200 transition-colors"
                >
                  Cancel
                  </motion.button>
              </div>
            </div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* Versions List */}
        <div className="space-y-2">
          <AnimatePresence>
          {groupings.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-10 sm:py-12 px-4 
                  rounded-xl border border-dashed border-gray-200 text-center"
              >
                <MapPinArea size={20} className="text-gray-300 mb-2 sm:mb-3 sm:w-6 sm:h-6" />
                <p className="text-gray-500 text-xs sm:text-sm font-medium mb-1">
                  No versions yet
                </p>
                <p className="text-[10px] sm:text-xs text-gray-400 mb-4">
                Create different versions for your locations
              </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                onClick={() => setIsCreating(true)}
                  className="bg-black text-white px-4 py-2 rounded-xl text-xs sm:text-sm font-medium 
                    hover:bg-gray-800 transition-colors"
              >
                Create First Version
                </motion.button>
              </motion.div>
          ) : (
              groupings.map((group, index) => {
              const assignedCount = (assignedLocations[group.id] || []).length;
              const isSelected = selectedGrouping === group.id;
              const isEditing = editingVariantId === group.id;

              return (
                <motion.div 
                  key={group.id}
                    custom={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="removed"
                  whileTap={isMobile ? { scale: 0.98 } : {}}
                  className={cn(
                      "group rounded-xl cursor-pointer transition-all duration-200 border",
                    isSelected
                        ? "bg-gray-50/80 border-gray-200/60 shadow-sm"
                        : "bg-white border-gray-100/80 hover:bg-gray-50/50 hover:border-gray-200/60"
                  )}
                >
                  <div 
                      className="p-3 sm:p-4"
                    onClick={() => !isEditing && handleSelectGrouping(group.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center min-w-0 flex-1">
                        <div className={cn(
                            "rounded-lg mr-3 p-2",
                            isSelected ? "bg-gray-100" : "bg-gray-50"
                        )}>
                          <MapPinArea className={cn(
                              "h-3 w-3 sm:h-4 sm:w-4",
                              isSelected ? "text-gray-600" : "text-gray-400"
                          )} />
                        </div>
                        <div className="min-w-0 flex-1">
                          {isEditing ? (
                            <div className="flex items-center space-x-1">
                              <input
                                type="text"
                                value={editingVariantName}
                                onChange={(e) => setEditingVariantName(e.target.value)}
                                  className="flex-1 min-w-0 text-xs sm:text-sm font-medium border border-gray-300 rounded-lg px-2 py-1 
                                    focus:ring-2 focus:ring-black/50 focus:border-transparent"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleEditVariantName(group.id);
                                  if (e.key === 'Escape') cancelEditingVariant();
                                }}
                                autoFocus
                              />
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                onClick={() => handleEditVariantName(group.id)}
                                  className="text-gray-500 hover:text-green-600 p-1 flex-shrink-0 rounded-full
                                    hover:bg-green-50"
                              >
                                <Check className="h-3 w-3" />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                onClick={cancelEditingVariant}
                                  className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0 rounded-full
                                    hover:bg-gray-50"
                              >
                                <X className="h-3 w-3" />
                                </motion.button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-xs sm:text-sm text-gray-900 truncate">
                                {group.name}
                              </h4>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingVariant(group);
                                }}
                                className={cn(
                                    "text-gray-400 hover:text-gray-600 p-1.5 rounded-lg transition-all",
                                  isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                                )}
                              >
                                <PencilLine className="h-3 w-3" />
                                </motion.button>
                            </div>
                          )}
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                            {assignedCount} location{assignedCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>

                      {!isEditing && groupings.length > 1 && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${group.name}"? This cannot be undone.`)) {
                              handleDeleteGrouping(group.id);
                            }
                          }}
                          className={cn(
                              "text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg p-2 transition-colors",
                              isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                          )}
                        >
                            <Trash className="h-3 w-3 sm:h-4 sm:w-4" />
                          </motion.button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

// Locations Tab Component
const LocationsTab = ({
  selectedGrouping,
  groupings,
  locations,
  assignedLocations,
  handleLocationToggle,
  setActiveTab,
  isMobile,
  itemVariants
}) => {
  const selectedGroup = groupings.find(g => g.id === selectedGrouping);
  const totalAssigned = new Set(Object.values(assignedLocations).flat()).size;

  if (!selectedGrouping) {
    return (
      <div className="flex-1 flex items-center justify-center p-5 sm:p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-sm mx-auto"
        >
          <MapPinArea size={20} className="text-gray-300 mx-auto mb-3 sm:mb-4 sm:w-6 sm:h-6" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Select a Version</h3>
          <p className="text-xs sm:text-sm text-gray-500 mb-4">
            {isMobile ? "Go to versions and select one to manage its locations" : "Choose a version from the sidebar to manage its locations"}
          </p>
          {isMobile && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab('versions')}
              className="bg-black text-white px-4 py-2 rounded-xl text-xs sm:text-sm font-medium 
                hover:bg-gray-800 transition-colors"
            >
              View Versions
            </motion.button>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-5 sm:p-6">
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="max-w-2xl"
      >
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          {isMobile && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveTab('versions')}
              className="flex items-center text-gray-600 mb-3 p-2 -ml-2 rounded-lg hover:bg-gray-50 
                active:scale-95 transition-all"
            >
              <CaretLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              <span className="text-xs sm:text-sm font-medium">Back to versions</span>
            </motion.button>
          )}
          <h3 className="font-medium text-base sm:text-lg text-gray-900 mb-2">
            {selectedGroup?.name}
          </h3>
          <p className="text-xs sm:text-sm text-gray-500">
            Choose which locations use this version
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-gray-100 rounded-full h-2 mb-3">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${locations.length > 0 ? totalAssigned / locations.length * 100 : 0}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-black h-2 rounded-full"
            />
          </div>
          <p className="text-xs sm:text-sm text-gray-500">
            {totalAssigned} of {locations.length} locations assigned
          </p>
        </div>

        {/* Locations Grid */}
        <AnimatePresence>
        {locations.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-10 sm:py-12 px-4 
                rounded-xl border border-dashed border-gray-200 text-center"
            >
              <MapPinArea size={20} className="text-gray-300 mx-auto mb-3 sm:mb-4 sm:w-6 sm:h-6" />
              <h4 className="text-sm sm:text-base font-medium text-gray-900 mb-2">No locations found</h4>
              <p className="text-xs sm:text-sm text-gray-500">Add locations to your account to create versions for them</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:gap-3">
              {locations.map((location, index) => {
              const isAssigned = assignedLocations[selectedGrouping]?.includes(location.id);

              return (
                <motion.div 
                  key={location.id}
                    custom={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="removed"
                  whileTap={isMobile ? { scale: 0.98 } : {}}
                  className={cn(
                      "rounded-xl border transition-all duration-200",
                    isAssigned
                        ? "bg-gray-50/80 border-gray-200/60 shadow-sm"
                        : "bg-white border-gray-100/80 hover:border-gray-200/60 hover:bg-gray-50/30"
                  )}
                >
                    <div className="flex items-center justify-between p-3 sm:p-4">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className={cn(
                          "rounded-lg p-2 sm:p-3 mr-3 sm:mr-4",
                          isAssigned ? "bg-gray-100" : "bg-gray-50"
                      )}>
                        <MapPinLine className={cn(
                            "h-4 w-4 sm:h-5 sm:w-5",
                            isAssigned ? "text-gray-600" : "text-gray-400"
                        )} />
                      </div>
                      <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-xs sm:text-sm text-gray-900 truncate">
                          {location.name}
                        </h4>
                          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                          {location.google_address ? 'Address configured' : 'No address'}
                        </p>
                      </div>
                    </div>

                    <Switch
                      checked={isAssigned}
                      onCheckedChange={() => handleLocationToggle(selectedGrouping, location.id)}
                        className="data-[state=checked]:bg-black"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default LocationVariantsModal; 